import type { BackendModule, ReadCallback } from 'i18next';
import i18n from 'i18next';
import { getApiBaseUrl } from '@/config/env';
import { getBundledNamespaceResource, normalizeLanguageCode } from '@/i18n/resources';

type JsonObject = Record<string, unknown>;

function isObject(value: unknown): value is JsonObject {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function applyFlatKey(target: JsonObject, key: string, value: unknown): void {
  const parts = key.split('.').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return;
  let cursor = target;
  for (const part of parts.slice(0, -1)) {
    const existing = cursor[part];
    if (!isObject(existing)) {
      cursor[part] = {};
    }
    cursor = cursor[part] as JsonObject;
  }
  cursor[parts[parts.length - 1]] = value;
}

function inflateFlatKeys(values: Record<string, unknown>): JsonObject {
  const inflated: JsonObject = {};
  for (const [key, value] of Object.entries(values)) {
    if (key.includes('.')) {
      applyFlatKey(inflated, key, value);
    } else {
      inflated[key] = value;
    }
  }
  return inflated;
}

async function loadRemoteResource(languageCode: string, namespace: string): Promise<JsonObject> {
  const response = await fetch(
    `${getApiBaseUrl()}/v1/i18n/${encodeURIComponent(languageCode)}/${encodeURIComponent(namespace)}`,
    {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Accept-Language': languageCode.replace('_', '-'),
      },
    },
  );
  if (!response.ok) {
    return {};
  }
  const body = (await response.json()) as Record<string, unknown>;
  return inflateFlatKeys(body);
}

export const lazyI18nBackend: BackendModule = {
  type: 'backend',
  init: () => {
    /* no-op */
  },
  read: (language: string, namespace: string, callback: ReadCallback) => {
    const languageCode = normalizeLanguageCode(language);
    const local = getBundledNamespaceResource(languageCode, namespace);
    // Serve bundled strings synchronously so first paint does not miss keys.
    callback(null, local);
    void loadRemoteResource(languageCode, namespace)
      .then((remote) => {
        if (!remote || Object.keys(remote).length === 0) return;
        i18n.addResourceBundle(languageCode, namespace, remote, true, true);
      })
      .catch(() => {
        /* bundled locale only */
      });
  },
};
